import { useState } from 'react'
import type { AIModel } from '@shared/types'
import { testApiKey } from '../services/api'

interface APIConfigProps {
  apiKey: string
  model: AIModel
  onApiKeyChange: (apiKey: string) => void
  onModelChange: (model: AIModel) => void
}

const MODEL_OPTIONS: { value: AIModel; label: string; description: string }[] = [
  { value: 'openai', label: 'OpenAI', description: 'GPT-4 Turbo' },
  { value: 'deepseek', label: 'DeepSeek', description: 'DeepSeek Chat' },
  { value: 'doubao', label: '豆包', description: 'Doubao Pro' },
  { value: 'qwen', label: '千问', description: 'Qwen Turbo' }
]

type TestStatus = 'idle' | 'testing' | 'success' | 'error'

export default function APIConfig({ apiKey, model, onApiKeyChange, onModelChange }: APIConfigProps) {
  const [testStatus, setTestStatus] = useState<TestStatus>('idle')
  const [testMessage, setTestMessage] = useState<string>('')

  const handleTestApi = async () => {
    if (!apiKey.trim()) {
      setTestStatus('error')
      setTestMessage('请先输入 API 密钥')
      return
    }

    setTestStatus('testing')
    setTestMessage('测试中...')

    const result = await testApiKey(apiKey, model)

    if (result.success) {
      setTestStatus('success')
      setTestMessage(result.message)
    } else {
      setTestStatus('error')
      setTestMessage(result.message)
    }

    // 3秒后自动隐藏消息
    setTimeout(() => {
      if (result.success) {
        setTestStatus('idle')
        setTestMessage('')
      }
    }, 3000)
  }

  const getStatusIndicator = () => {
    switch (testStatus) {
      case 'testing':
        return (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="w-3 h-3 rounded-full bg-blue-600 animate-pulse"></div>
            <span>测试中...</span>
          </div>
        )
      case 'success':
        return (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <div className="w-3 h-3 rounded-full bg-green-600"></div>
            <span>✓ {testMessage}</span>
          </div>
        )
      case 'error':
        return (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <div className="w-3 h-3 rounded-full bg-red-600"></div>
            <span>✗ {testMessage}</span>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">API 配置</h2>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-900">
          选择 AI 模型
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {MODEL_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onModelChange(option.value)
                setTestStatus('idle')
                setTestMessage('')
              }}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                model === option.value
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-primary-300'
              }`}
            >
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs text-gray-500 mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-900">
          API 密钥
        </label>
        <div className="flex gap-2">
          <input
            type="password"
            value={apiKey}
            onChange={(e) => {
              onApiKeyChange(e.target.value)
              setTestStatus('idle')
              setTestMessage('')
            }}
            placeholder="输入 API 密钥..."
            className="input font-mono text-sm flex-1"
          />
          <button
            onClick={handleTestApi}
            disabled={!apiKey.trim() || testStatus === 'testing'}
            className="btn btn-secondary px-4 whitespace-nowrap"
          >
            {testStatus === 'testing' ? '测试中...' : '测试连接'}
          </button>
        </div>

        {/* 状态指示器 */}
        <div className="min-h-[1.5rem]">
          {getStatusIndicator()}
        </div>

        <p className="text-xs text-gray-500">
          密钥仅在请求时使用,不会被存储。点击"测试连接"验证配置是否正确。
        </p>
      </div>
    </div>
  )
}
